import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from "../model/types";
/**
 * File validation service
 */
export class FileValidationService {
    /**
     * Validate file type
     */
    static validateFileType(mimeType) {
        return ALLOWED_FILE_TYPES.includes(mimeType);
    }
    /**
     * Validate file size
     */
    static validateFileSize(size, maxSize = MAX_FILE_SIZE) {
        return size > 0 && size <= maxSize;
    }
    /**
     * Validate file name
     */
    static validateFileName(filename) {
        // Basic filename validation
        if (!filename || filename.trim().length === 0)
            return false;
        if (filename.length > 255)
            return false;
        // Check for dangerous characters
        const dangerousChars = /[<>:"/\\|?*\x00-\x1f]/;
        if (dangerousChars.test(filename))
            return false;
        // Check for reserved names (Windows)
        const reservedNames = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(\.|$)/i;
        if (reservedNames.test(filename))
            return false;
        return true;
    }
    /**
     * Comprehensive file validation
     */
    static validateFile(file) {
        const errors = [];
        if (!this.validateFileName(file.name)) {
            errors.push("Invalid filename");
        }
        if (file.type && !this.validateFileType(file.type)) {
            errors.push("File type not allowed");
        }
        if (!this.validateFileSize(file.size)) {
            errors.push(`File size exceeds limit (${MAX_FILE_SIZE / 1024 / 1024}MB)`);
        }
        return {
            isValid: errors.length === 0,
            errors,
        };
    }
    /**
     * Get file type category
     */
    static getFileCategory(mimeType) {
        if (mimeType.startsWith("image/"))
            return "image";
        if (mimeType.includes("pdf") || mimeType.includes("document") || mimeType.includes("text"))
            return "document";
        if (mimeType.includes("zip") || mimeType.includes("archive"))
            return "archive";
        return "other";
    }
    /**
     * Get human-readable file size
     */
    static formatFileSize(bytes) {
        if (bytes === 0)
            return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    }
    /**
     * Get file extension from filename
     */
    static getFileExtension(filename) {
        const lastDot = filename.lastIndexOf(".");
        return lastDot > 0 ? filename.substring(lastDot + 1).toLowerCase() : "";
    }
    /**
     * Generate safe filename
     */
    static sanitizeFilename(filename) {
        // Remove dangerous characters and replace with underscores
        return filename
            .replace(/[<>:"/\\|?*\x00-\x1f]/g, "_")
            .replace(/\s+/g, "_")
            .replace(/_+/g, "_")
            .trim();
    }
}
/**
 * File upload service
 */
export class FileUploadService {
    /**
     * Prepare file for upload
     */
    static prepareFileUpload(file) {
        return {
            filename: file.name,
            fileSize: file.size,
            mimeType: file.type,
        };
    }
    /**
     * Upload file using direct upload method
     */
    static async uploadFileDirect(workspaceId, file, taskId, uploadUrl, onProgress) {
        try {
            const formData = new FormData();
            formData.append("file", file);
            const xhr = new XMLHttpRequest();
            return new Promise((resolve, reject) => {
                xhr.upload.addEventListener("progress", (event) => {
                    if (event.lengthComputable && onProgress) {
                        const progress = (event.loaded / event.total) * 100;
                        onProgress(progress);
                    }
                });
                xhr.addEventListener("load", () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            const response = JSON.parse(xhr.responseText);
                            resolve({
                                success: true,
                                message: "File uploaded successfully",
                                file: response,
                            });
                        }
                        catch (error) {
                            resolve({
                                success: true,
                                message: "File uploaded successfully",
                            });
                        }
                    }
                    else {
                        reject({
                            success: false,
                            error: `Upload failed with status ${xhr.status}`,
                        });
                    }
                });
                xhr.addEventListener("error", () => {
                    reject({
                        success: false,
                        error: "Upload failed due to network error",
                    });
                });
                xhr.open("PUT", uploadUrl);
                xhr.setRequestHeader("x-filename", file.name);
                xhr.setRequestHeader("x-task-id", taskId);
                //  added to equal createHeaders function
                xhr.setRequestHeader("X-Workspace-ID", workspaceId);
                xhr.setRequestHeader("content-type", file.type);
                xhr.send(file);
            });
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : "Upload failed",
            };
        }
    }
    /**
     * Calculate upload progress
     */
    static calculateProgress(loaded, total) {
        return total > 0 ? Math.round((loaded / total) * 100) : 0;
    }
}
/**
 * File download service
 */
export class FileDownloadService {
    /**
     * Download file using browser download
     */
    static async downloadFile(downloadUrl, filename) {
        try {
            // Create a temporary link element and trigger download
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = filename;
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return {
                success: true,
                message: "Download started",
            };
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : "Download failed",
            };
        }
    }
    /**
     * Open file in new tab (for viewable files)
     */
    static openFileInNewTab(downloadUrl) {
        window.open(downloadUrl, "_blank");
    }
    /**
     * Check if file can be previewed in browser
     */
    static canPreviewFile(mimeType) {
        const previewableTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/gif",
            "image/webp",
            "application/pdf",
            "text/plain",
            "text/csv",
        ];
        return previewableTypes.includes(mimeType);
    }
}
/**
 * File management service
 */
export class FileManagementService {
    /**
     * Get file icon based on type
     */
    static getFileIcon(mimeType) {
        const category = FileValidationService.getFileCategory(mimeType);
        switch (category) {
            case "image":
                return "🖼️";
            case "document":
                return "📄";
            case "archive":
                return "📦";
            default:
                return "📎";
        }
    }
    /**
     * Sort files by different criteria
     */
    static sortFiles(files, sortBy = "date", order = "desc") {
        const sorted = [...files].sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case "name":
                    comparison = a.filename.localeCompare(b.filename);
                    break;
                case "date":
                    comparison = new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime();
                    break;
                case "size":
                    comparison = (a.fileSize || 0) - (b.fileSize || 0);
                    break;
            }
            return order === "asc" ? comparison : -comparison;
        });
        return sorted;
    }
    /**
     * Filter files by type
     */
    static filterFilesByType(files, category = "all") {
        if (category === "all")
            return files;
        return files.filter((file) => {
            if (!file.mimeType)
                return category === "other";
            return FileValidationService.getFileCategory(file.mimeType) === category;
        });
    }
    /**
     * Search files by filename
     */
    static searchFiles(files, query) {
        if (!query.trim())
            return files;
        const searchTerm = query.toLowerCase().trim();
        return files.filter((file) => file.filename.toLowerCase().includes(searchTerm));
    }
}
